import React, {FC, forwardRef, useCallback, useEffect, useState} from "react";
import {Link, useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {addressService, tokenService} from "../../store/actions";
import XRC720TokenTransferList from "../common/xrc20-token-transfer-list";
import XRC720TokenHolderList from "../common/xrc20-token-holder-list";
import {
    baseUnit,
    formatNumber,
    formatPercent,
    formatSign,
    formatUnit,
    getFiat,
    serializeQuery,
    toXDCFormatNumber,
    toXDCNumber,
    toXDCNumberWithoutFormat,
    toXDCNumberWoutFormat
} from "../../common/utils/function";
import useCommon from "../../common/hooks/useCommon";
import ContractDetails from "../common/contract-details";
import XRC721InventoryList from "../common/xrc721-inventory-list";
import {useSelector} from "react-redux";
import BCopy from "../../components/b-copy";
import BTooltip from "../../components/b-tooltip";
import Ads from "../common/ads";
import Info from "../common/info";
import MarketTable from "../common/market";
import {Button, Dropdown, Form} from "react-bootstrap";
import axios from "axios";
import {useTranslation} from "react-i18next";
import DEXTrades from "../common/dex-trades";
import NFTTrades from "../common/nft-trades";
import CMC from "../../images/cmc_new.svg";
import CG from "../../images/coingecko_new.svg";
import Sponsor from "../common/sponsor";
import TokenImage from "../common/token-image";
import BSkeleton from "../../components/b-skeleton";
import MSK from "../../images/MetaMask_Fox.svg";
import FB from "../../images/facebook.svg";
import TW from "../../images/twitter.svg";
import GTH from "../../images/GitHub.svg";
import YT from "../../images/YouTube.svg";
import MD from "../../images/medium.svg";
import RD from "../../images/reddit.svg";
import TG from "../../images/Telegram.svg";
import TokenHoldingLineChart from "./common/tokenHoldingChart";
import moment from "moment";
import AnalysisTab from "../common/analysis";

interface TransactionListData {
    name: string;
    icon_url: string;
    usd: number | string;
    type: string;
    fiat_value: any;
    exchange_rate: any;
    price_change_24h: string;
    total_supply: any;
    symbol: string;
    holders: string;
    transactionCount: string;
    address: string;
    decimals: number;
    circulating_market_cap: any;
    circulating_market_cap_usd: any;
    categories: any;
    coingecko_data: any;
    stats: any;
    token_social: any;
}

interface Token {
    address: string;
    symbol: string;
}

interface ApiResponseItem {
    token: Token;
}

const TokenDetails: FC = () => {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const {token, holder} = useParams();
    const {t} = useTranslation();
    const [searchVal, setSearchVal] = useState('')

    const address = searchParams.get("address");
    const {getXDC, get0x} = useCommon();
    const fiat = useSelector((state: any) => state.common.fiat) || 'usd';
    const _stats = useSelector((state: any) => state.common.stats);
    const _tokenType = useSelector((state: any) => state.common.tokenType);
    const [loading, setLoading] = useState(true);
    const [holdinglist, setHoldingList] = useState<any>(null);
    const [transferCount, setTransferHolderCount] = useState({
        token_holders_count: "",
        transfer_count: "",
        last_24h_transfer_count: "",
        last_24h_holders_count: "",
        last_24h_percentage_change: ""
    });

    const [stats, setStats] = useState<TransactionListData>({
        name: "",
        icon_url: "",
        usd: 0,
        type: "",
        fiat_value: "0",
        exchange_rate: "0",
        price_change_24h: "0",
        total_supply: "0",
        symbol: "",
        holders: "",
        transactionCount: "",
        address: "",
        decimals: 0,
        circulating_market_cap: 0,
        circulating_market_cap_usd: 0,
        categories: [],
        coingecko_data: [],
        stats: [],
        token_social: [],
    });

    const [contractStats, setContractStats] = useState<any>({});
    const [addressStats, setAddressStats] = useState<any>({});
    const [extra, setExtra] = useState<any>({});

    const getIcons = useCallback((str?: string) => {
        const name = str?.toLowerCase() ?? '';
        const icons: Record<string, JSX.Element> = {
            email: <i className="bi bi-envelope-fill me-2"></i>,
            telegram: <img src={TG} alt="Telegram" height={23} width={19} className="me-2"/>,
            reddit: <img src={RD} alt="Reddit" height={32} width={26} className="me-2"/>,
            medium: <img src={MD} alt="Medium" height={28} width={23} className="me-2"/>,
            youtube: <img src={YT} alt="YouTube" height={20} width={20} className="me-2"/>,
            twitter: <img src={TW} alt="Twitter" height={18} width={18} className="me-2"/>,
            facebook: <img src={FB} alt="Facebook" height={24} width={23} className="me-2"/>,
            github: <img src={GTH} alt="GitHub" height={18} width={18} className="me-2"/>,
            coingecko: <img src={CG} alt="CoinGecko" height={20} width={20} className="me-2"/>,
            coinmarketcap: <img src={CMC} alt="CoinMarketCap" height={20} width={20} className="me-2"/>,
            discord: <i className="bi bi-discord me-3"></i>,
            default: <i className="bi bi-link-45deg me-3"></i>,
        };
        return icons[name] || icons.default;
    }, []);

    const [tab, setTab] = useState<string>("");

    useEffect(() => {
        const hash = location.hash.substring(1);
        if (["", "balances", "inventory", "tokenTrade", "tokenNFTTrade", "info", "market", "code", "readContract", "readProxyContract", "writeContract", "writeProxyContract", "aiAuditAnalysis", "analysis"].includes(hash)) {
            setTab(hash);
        } else {
            setTab("");
        }
    }, [location.hash]);

    const handleTab = (e: string) => {
        setTab(e);
        window.location.hash = e.length ? `#${e}` : '';
    };

    const fetchTokenData = async () => {
        try {
            setLoading(true);

            const tokenResponse = await tokenService.getToken(token || holder);
            setStats(tokenResponse);

            let extraData = {
                symbol: tokenResponse.symbol,
                website: tokenResponse?.coingecko_data?.links?.homepage[0] || '',
                communities: {
                    twitter: `https://twitter.com/${tokenResponse?.coingecko_data?.links?.twitter_screen_name || ''}`,
                    telegram: `https://t.me/${tokenResponse?.coingecko_data?.links?.telegram_channel_identifier || ''}`,
                    instagram: tokenResponse?.coingecko_data?.links?.official_forum_url[0] || '',
                    coinmarketcap: tokenResponse?.coingecko_data?.links?.official_forum_url[1] || ''
                },
                links: tokenResponse?.coingecko_data?.links?.blockchain_site.filter((site: string) => site) || []
            };
            try {
                const responseJSON = await axios.get(`${process.env.REACT_APP_BASE_URL}/tokens/config/${tokenResponse.symbol}.json`);
                extraData = {
                    ...extraData,
                    symbol: responseJSON?.data?.symbol || extraData.symbol,
                    website: responseJSON?.data?.website || extraData.website,
                    communities: {
                        ...extraData.communities,
                        ...responseJSON?.data?.communities
                    },
                    links: [...extraData.links, ...(responseJSON?.data?.links || [])]
                };
            } catch (apiError) {
                console.error("Error fetching token details from JSON API:", apiError);
            }

            setExtra(extraData);

            const addressResponse = await addressService.getAddress(token || holder);
            setAddressStats(addressResponse);
            setLoading(false);
        } catch (err) {
            console.error(err);
            setLoading(false);
        }
    };

    const fetchTransferHolderCount = async () => {
        try {
            const response = await tokenService.getTransferHodlerCount(token || holder);
            setTransferHolderCount(response);
        } catch (err) {
            console.log(err);
        }
    };

    const fetchAddress = () => {
        addressService.getAddressTokens(address, serializeQuery({type: stats?.type ?? "ERC-20"}))
            .then(res => {
                const data = res.items.find((e: any) => (e.token.address?.toLowerCase() === (token || holder)?.toLowerCase()));
                setContractStats(data);
            })
            .catch(err => console.error(err));
    };

    const getTokenHoldingData = async () => {
        try {
            const fromDate = moment().subtract(15, 'days').format('YYYY-MM-DD');
            const toDate = moment().format('YYYY-MM-DD');
            const chartData = await tokenService.getTokenHolding(token || holder, fromDate, toDate);
            setHoldingList(chartData);
        } catch (err) {
            console.error(err);
        }
    }

    useEffect(() => {
        getTokenHoldingData();
    }, []);

    useEffect(() => {
        if (address && stats?.type?.length) fetchAddress();
    }, [address, stats?.type]);

    useEffect(() => {
        fetchTokenData();
        fetchTransferHolderCount();
    }, [holder]);

    const addTokenToMetaMask = async () => {
        const tokenAddress = stats?.address;
        const tokenSymbol = stats?.symbol;
        const tokenDecimals = stats?.decimals;
        const tokenImage = stats?.icon_url;

        try {
            if (typeof window.ethereum !== 'undefined') {
                const wasAdded = await window.ethereum.request({
                    method: 'wallet_watchAsset',
                    params: {
                        type: 'ERC20',
                        options: {
                            address: tokenAddress,
                            symbol: tokenSymbol,
                            decimals: tokenDecimals,
                            image: tokenImage,
                        },
                    },
                });

                if (wasAdded) {
                    console.log('Token added to MetaMask');
                } else {
                    console.log('Token not added to MetaMask');
                }
            } else {
                console.log('MetaMask is not installed');
            }
        } catch (error) {
            console.error('Error adding token to MetaMask', error);
        }
    };

    interface CustomMenuProps {
        children: React.ReactNode;
        style: React.CSSProperties;
        className: string;
        'aria-labelledby': string;
    }

    const CustomMenu = forwardRef<HTMLDivElement, CustomMenuProps>(
        ({children, style, className, 'aria-labelledby': labeledBy}, ref) => {
            const [searchValue, setSearchValue] = useState('');
            const {getXDC, get0x} = useCommon();
            const location = useLocation();
            const navigate = useNavigate();

            const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
                event.preventDefault();
                if (searchValue.length >= 66) {
                    navigate(`/tx/${get0x(searchValue)}`);
                } else if (searchValue.length >= 42) {
                    navigate(`/token/${get0x(token || holder)}?address=${get0x(searchValue)}`);
                } else {
                    console.log('Value must be at least 42 characters long');
                }
                setSearchVal(searchValue);
            };

            return (
                <div ref={ref} style={style} className={className} aria-labelledby={labeledBy}>
                    <Form onSubmit={handleSearch} className="custom-form p-2">
                        <Form.Control
                            type="text"
                            placeholder="Filter Address / Txn Hash"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            className="flex-grow-1 custom-input"
                            maxLength={100}
                            style={{minWidth: '200px', minHeight: '38px'}}
                        />
                        <Button type="submit" style={{minHeight: '38px'}}>
                            Find
                        </Button>
                    </Form>
                    {children}
                </div>
            );
        }
    );

    const [showAll, setShowAll] = useState(false);
    const itemsToShow = showAll ? stats?.categories : stats?.categories?.slice(0, 5);

    const handleShowAll = () => {
        setShowAll(true);
    };

    interface SocialLink {
        key: string;
        url: string;
    }

    const generateSocialDropdownItems = (socialData: Record<string, string> | undefined) => {
        if (!socialData) return null;

        let websiteLink: string | undefined = socialData.website;
        const specialLinks = ['coingecko', 'coinmarketcap'];

        const specialItems: SocialLink[] = [];
        const otherItems: SocialLink[] = [];

        Object.keys(socialData).forEach(key => {
            let url = socialData[key];
            if (!url) return;

            if (key === 'website') {
                websiteLink = url;
            } else if (key === 'telegram') {
                url = `https://t.me/${url}`;
            }

            if (key === 'website') {
                websiteLink = url;
            } else if (specialLinks.includes(key)) {
                specialItems.push({key, url});
            } else {
                otherItems.push({key, url});
            }
        });

        return (
            <React.Fragment>
                {websiteLink && (
                    <Dropdown.Item href={websiteLink} target="_blank" className="bs-small-font">
                        {getIcons('default')}
                        {new URL(websiteLink).hostname}
                    </Dropdown.Item>
                )}
                {websiteLink && (specialItems.length > 0 || otherItems.length > 0) && <Dropdown.Divider/>}
                {specialItems.length > 0 && (
                    <React.Fragment>
                        {specialItems.map(({key, url}) => (
                            <Dropdown.Item href={url} target="_blank" className="bs-small-font text-capitalize" key={key}>
                                {getIcons(key)}
                                {key.charAt(0).toUpperCase() + key.slice(1)}
                            </Dropdown.Item>
                        ))}
                        {otherItems.length > 0 && <Dropdown.Divider/>}
                    </React.Fragment>
                )}
                {otherItems.map(({key, url}) => (
                    <Dropdown.Item href={url} target="_blank" className="bs-small-font text-capitalize" key={key}>
                        {getIcons(key)}
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                    </Dropdown.Item>
                ))}
            </React.Fragment>
        );
    };

    return (
        <div className="py-3">
            <div className="container">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="bs-page-title bs-font">
                            {!loading && (
                                <TokenImage image={stats?.icon_url} symbol={stats?.symbol} params={{width: 32, className: "me-2 rounded-5"}} bg/>
                            )}
                            {t("Token")}
                            {loading ? (
                                <BSkeleton height={14} width={100}/>
                            ) : (
                                <span className="bs-page-title-secondary bs-font">
                                    {stats?.name} ({stats?.symbol})
                                </span>
                            )}
                        </div>
                        <Sponsor/>
                        <div className="d-md-flex justify-content-between align-items-center">
                            <div className="d-flex flex-wrap my-3">
                                {loading ? (
                                    <div className="bs-badge h-gray rounded-pill px-2 m-1">
                                        <BSkeleton height={8} width={60}/>
                                    </div>
                                ) : (
                                    <React.Fragment>
                                        <div className="bs-badge h-gray rounded-pill px-2 m-1">
                                            {stats?.type === "ERC-20"
                                                ? `${_tokenType}-20`
                                                : stats?.type === "ERC-404"
                                                    ? `${_tokenType}-404`
                                                    : stats?.type === "ERC-721"
                                                        ? `${_tokenType}-721`
                                                        : `${_tokenType}-1155`}
                                        </div>
                                        {itemsToShow?.map((e: any, index: any) => (
                                            <Link to={`/tokens?category=${e.slug}`} key={index}>
                                                <div className="bs-badge h-gray rounded-pill px-2 m-1">#{e?.name}</div>
                                            </Link>
                                        ))}
                                        {!showAll && stats?.categories?.length > 5 && (
                                            <div className="bs-badge h-gray rounded-pill px-2 m-1" onClick={handleShowAll} style={{cursor: 'pointer'}}>
                                                5+ tags
                                            </div>
                                        )}
                                    </React.Fragment>
                                )}
                            </div>
                            {extra?.website && (
                                <div className="d-flex align-items-center gap-1 my-3">
                                    <Dropdown className="bs_token-social" align="end">
                                        <Dropdown.Toggle id="dropdown-button-dark-example1" variant="white" size="sm">
                                            <i className="bi bi-globe2 me-2 bs-font-primary"></i>
                                            {extra.website?.split("://")?.[1]}
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu className="custom-menu">
                                            {generateSocialDropdownItems(stats?.token_social)}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                    <Dropdown className="d-flex bs_token-social" align="end">
                                        <Dropdown.Toggle id="dropdown-button-dark-example1" variant="white" size="sm">
                                            <i className="bi bi-list-check"></i>
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu variant="dark" align="start" className="custom-menu">
                                            <Dropdown.Item href="/tokencheck-tool" className="bs-small-font">
                                                <i className="bi bi-clock-history me-1"></i> Check previous token supply
                                            </Dropdown.Item>
                                            <Dropdown.Item onClick={addTokenToMetaMask} className="bs-small-font" rel="nofollow">
                                                <img src={MSK} alt="MetaMask Icon" height={16} width={16} style={{marginBottom: '2px'}} className="me-1"/>
                                                Add Token to MetaMask (Web3)
                                            </Dropdown.Item>
                                            <Dropdown.Item href="https://github.com/BlocksScan/assets/tree/main/tokens/config" className="bs-small-font" target="_blank"
                                                           rel="noopener noreferrer">
                                                <i className="bi bi-pencil me-1"></i> Update Token Info
                                            </Dropdown.Item>
                                            <Dropdown.Item href="https://github.com/BlocksScan/assets?tab=readme-ov-file#token-information-requirements" className="bs-small-font"
                                                           target="_blank" rel="noopener noreferrer">
                                                <i className="fa-solid fa-user-tag"></i>Update Name Tag or Label
                                            </Dropdown.Item>
                                            <Dropdown.Item href="https://github.com/BlocksScan/assets?tab=readme-ov-file#adding-a-new-account-tag" className="bs-small-font"
                                                           target="_blank" rel="noopener noreferrer">
                                                <i className="bi bi-fire me-1"></i> Submit Burn Details
                                            </Dropdown.Item>
                                            <Dropdown.Item href="https://github.com/BlocksScan/assets?tab=readme-ov-file#adding-a-new-account-tag" className="bs-small-font"
                                                           target="_blank" rel="noopener noreferrer">
                                                <i className="bi bi-flag me-1"></i> Report/Flag Address
                                            </Dropdown.Item>
                                            <Dropdown.Item href="/mytokenignore" className="bs-small-font" target="_blank" rel="noopener noreferrer">
                                                <i className="bi bi-eye-slash me-1"></i> Add to Ignore List
                                            </Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-lg-4 mb-3">
                        <div className="bs-card h-100">
                            <div className="bs-card-body p-3">
                                <div className="d-flex align-items-center justify-content-between pb-2">
                                    <h2 className="bs-card-header-title">
                                        {t("Overview")}
                                    </h2>
                                </div>
                                <div className="py-2">
                                    <h2 className="text-secondary mb-0">{t("Max Total Supply")}</h2>
                                    {loading ? <BSkeleton height={14}/> : <span>
                                        {!stats?.total_supply && <BTooltip text={t("Note: Filtered by  totalSupply() , method returned a zero value.")}>
                                            <i className="bi bi-exclamation-circle-fill me-2 text-warning bs-cp"></i>
                                        </BTooltip>}
                                        {formatUnit(toXDCFormatNumber(stats?.total_supply ?? "0", stats?.decimals), stats?.symbol)}
                                    </span>}
                                </div>
                                <div className="py-2">
                                    <h2 className="text-secondary mb-0">{t("Holders")}</h2>
                                    {loading ? (
                                        <BSkeleton height={14}/>
                                    ) : (
                                        <React.Fragment>
                                            <div className="d-flex align-items-center flex-wrap">
                                                <span className="me-2">
                                                    {formatNumber(stats?.stats?.holder_count ?? transferCount?.token_holders_count)}
                                                    &nbsp;
                                                    <BTooltip
                                                        text={`A ${stats?.stats?.last_24h_holders_count ?? transferCount?.token_holders_count} increase in token holder from previous day count of ${formatNumber(Number(stats?.stats?.holder_count ?? transferCount?.token_holders_count) - Number(stats?.stats?.last_24h_holders_count ?? transferCount?.last_24h_holders_count))}`}>
                                                        {(!stats?.stats || stats?.stats?.last_24h_holders_count === 0) ? (
                                                            <span className="small text-black text-nowrap">(0%)</span>
                                                        ) : (
                                                            <span className="small text-success text-nowrap">
                                                                {formatNumber(transferCount?.last_24h_holders_count ?? 0) < 0 ? (
                                                                    <i className="bi bi-caret-down-fill text-danger"></i>
                                                                ) : (
                                                                    <i className="bi bi-caret-up-fill text-success"></i>
                                                                )}
                                                                <span
                                                                    className={`${formatNumber(transferCount?.last_24h_holders_count ?? 0) < 0 ? "text-danger" : "text-success"}`}>
                                                                    {formatPercent(Number(transferCount?.last_24h_holders_count ?? 0), Number(transferCount?.token_holders_count ?? 0), 2)}%
                                                                </span>
                                                            </span>
                                                        )}
                                                    </BTooltip>
                                                </span>
                                                <span className="ms-2 flex-grow-1" style={{maxWidth: "calc(100% - 200px)"}}>
                                                    <div className="w-100">
                                                        <TokenHoldingLineChart data={holdinglist} token={token || holder}/>
                                                    </div>
                                                </span>
                                            </div>
                                        </React.Fragment>
                                    )}
                                </div>
                                <div className="py-2">
                                    <h2 className="text-secondary mb-0">{t("Total Transfers")}</h2>
                                    {loading ? <BSkeleton height={14}/> : <span>
                                        {formatNumber(stats?.stats?.token_transfers_count ?? transferCount?.transfer_count)}
                                        <BTooltip text={"This count of token transfers is updated every few hours instead of in real-time"}>
                                            <i className="bi bi-exclamation-circle text-secondary ms-1"></i>
                                        </BTooltip>
                                    </span>}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-4 mb-3">
                        <div className="bs-card h-100">
                            <div className="bs-card-body p-3">
                                <div className="d-flex align-items-center justify-content-between pb-2">
                                    <h2 className="bs-card-header-title">{t("Market")}</h2>
                                </div>
                                <div className="py-2">
                                    <h2 className="text-secondary mb-0">Price</h2>
                                    {loading ? <BSkeleton height={14}/> : <span>$ {formatNumber((stats?.fiat_value ?? stats?.exchange_rate) ?? "-", 6) === "NaN" ? "0" : formatNumber((stats?.fiat_value ?? stats?.exchange_rate) ?? "-", 6)}
                                        &nbsp;@{formatNumber((stats?.fiat_value ?? stats?.exchange_rate) / _stats?.coin_price, 5) === "0" ? "0.00" : formatNumber((stats?.fiat_value ?? stats?.exchange_rate) / _stats?.coin_price, 5)} {baseUnit()}
                                        <span className="small text-success text-nowrap ms-1">
                                            <span
                                                className={`${formatNumber(stats?.price_change_24h ?? "") < 0 ? "red" : "green"}`}>({stats?.price_change_24h ? formatSign(formatNumber(stats?.price_change_24h ?? "", 2)) : 0}{""}%)</span>
                                        </span>
                                    </span>}
                                </div>
                                <div className="py-2">
                                    <h2 className="text-secondary mb-0">
                                        {t("ONCHAIN MARKET CAP")}
                                        <BTooltip
                                            text={`Calculated by multiplying the token's Total Supply on ${process.env.REACT_APP_BASE_NAME}  with the current market price per token. Numbers may be unrealistic if the onchain supply is much larger than the reported circulating supply.`}>
                                            <i className="bi bi-question-circle me-2 ps-2"></i>
                                        </BTooltip>
                                    </h2>
                                    {loading ? <BSkeleton height={14}/> : <span>
                                        {getFiat(toXDCNumberWoutFormat(stats?.total_supply, stats?.decimals) * Number(stats?.fiat_value ?? stats?.exchange_rate), 2, "USD", true)}
                                    </span>}
                                </div>
                                <div className="py-2">
                                    <h2 className="text-secondary mb-0">{t("Circulating Supply Market Cap")}</h2>
                                    {loading ? <BSkeleton height={14}/> : <span>{getFiat(stats?.circulating_market_cap_usd, 2, "USD", true)}</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-4 mb-3">
                        <div className="bs-card h-100">
                            <div className="bs-card-body p-3">
                                <div className="d-flex align-items-center justify-content-between pb-2">
                                    <h2 className="bs-card-header-title">{t("Other Info")}</h2>
                                </div>
                                <div className="py-2">
                                    <h2 className="text-secondary mb-0">
                                        {t("Token Contract")}
                                        {stats?.decimals ? `(${t("With")} ${stats.decimals} ${t("decimals")})` : null}
                                    </h2>
                                    {loading ? <BSkeleton height={14}/> : <span>
                                        <BTooltip text={t("Contract")} options={{position: "top center"}}>
                                            <i className="bi bi-file-earmark-text"></i>{" "}
                                        </BTooltip>
                                        <BTooltip text={stats?.address} options={{position: "top center"}}>
                                            <Link to={`/address/${get0x(stats?.address)}`}>{getXDC(stats?.address)}</Link>
                                        </BTooltip>
                                        <BCopy value={stats?.address}/>
                                    </span>}
                                </div>
                                <div className="py-2">
                                    <Ads/>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {!!address && (
                    <div className="bs-card mb-3 shadow-none">
                        <div className="bs-card-body p-3">
                            <div className="row gx-4">
                                <div className="col-md-12 col-lg-4">
                                    <p className="bs-font-secondary">{t("FILTERED BY TOKEN HOLDER")}</p>
                                    {loading ? <BSkeleton height={14}/> : (
                                        <p className="bs-font d-flex align-items-center">
                                            <p className="text-truncate">
                                                <Link to={`/address/${get0x(address)}`}>{getXDC(address)}</Link>
                                            </p>
                                            <BCopy value={address}/>
                                        </p>
                                    )}
                                </div>
                                <div className="col-md-12 col-lg-4 border-start">
                                    <p className="bs-font-secondary">{t("BALANCE")}</p>
                                    {loading ? <BSkeleton height={14}/> : (
                                        <p className="bs-font">
                                            {toXDCNumber(contractStats?.value, contractStats?.token?.decimals, 2)} {contractStats?.token?.symbol}
                                        </p>
                                    )}
                                </div>
                                {(stats?.type !== "ERC-20" && stats?.type !== "ERC-404") && <div className="col-md-12 col-lg-4 border-start">
                                    <p className="bs-font-secondary">{t("Value")}</p>
                                    {loading ? <BSkeleton height={14}/> : (
                                        <p className="bs-font">
                                            {getFiat(toXDCNumberWithoutFormat(contractStats?.value, contractStats?.token?.decimals) * Number(contractStats?.token?.exchange_rate ?? 0), 18, fiat, true)}
                                            &nbsp;(
                                            ~{formatNumber(toXDCNumberWithoutFormat(contractStats?.value, contractStats?.token?.decimals) / _stats?.coin_price, 2)} {baseUnit()})
                                        </p>
                                    )}
                                </div>}
                            </div>
                        </div>
                    </div>
                )}
                <div className="row">
                    <div className="col-lg-12">
                        <div className="bs-tab d-flex justify-content-between align-items-center">
                            <div>
                                <button
                                    className={`bs-tab-item ${[""].includes(tab) ? "active" : ""}`}
                                    onClick={() => handleTab("")}
                                >
                                    {t("Transfers")}
                                </button>
                                <button
                                    className={`bs-tab-item ${["balances"].includes(tab) ? "active" : ""}`}
                                    onClick={() => handleTab("balances")}
                                >
                                    {t("Holders")}
                                </button>
                                {(stats?.type === "ERC-721" || stats?.type === "ERC-1155") && (
                                    <button
                                        className={`bs-tab-item ${["inventory"].includes(tab) ? "active" : ""}`}
                                        onClick={() => handleTab("inventory")}
                                    >
                                        {t("Inventory")}
                                    </button>
                                )}
                                <button
                                    className={`bs-tab-item ${["info"].includes(tab) ? "active" : ""}`}
                                    onClick={() => handleTab("info")}
                                >
                                    {t("Info")}
                                </button>
                                {stats?.coingecko_data?.tickers && (<button
                                    className={`bs-tab-item ${["market"].includes(tab) ? "active" : ""}`}
                                    onClick={() => handleTab("market")}
                                >
                                    {t("Market")}
                                </button>)}
                                {addressStats?.is_contract && (
                                    <button
                                        className={`bs-tab-item ${["code", "readContract", "readProxyContract", "writeContract", "writeProxyContract", "aiAuditAnalysis"].includes(tab) ? "active" : ""}`}
                                        onClick={() => handleTab("code")}
                                    >
                                        {t("Contract")}
                                        {addressStats?.is_verified && <i className="fa fa-check-circle bs_verified bs_verified-contract"></i>}
                                    </button>
                                )}
                                <button
                                    className={`bs-tab-item ${["analysis"].includes(tab) ? "active" : ""}`}
                                    onClick={() => handleTab("analysis")}
                                >
                                    {t("Analysis")}
                                </button>
                            </div>
                            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                    {!!address && (<div className="badge bg-light text-dark border d-flex gap-1 align-items-baseline text-nowrap rounded-pill px-2 ">
                                        <span className="hash-tag text-truncate">{searchVal || address}</span>
                                        <a className="btn btn-sm rounded-circle d-flex content-center me-n1 bs-font-secondary" style={{width: "1.35rem", height: "1.35rem"}}
                                           href={`/token/${get0x(token || holder)}`}>
                                            <i className="bi bi-x-circle fa-fw"></i></a>
                                    </div>)}
                                    <div>
                                        <div>
                                            <Dropdown className="bs-search-btn">
                                                <Dropdown.Toggle id="dropdown-button-dark-example1" variant="white" size="sm">
                                                    <i className="bi bi-search"></i>
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu as={CustomMenu} style={{minWidth: '20rem'}}>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="bs-tab-content">
                            {[""].includes(tab) && (<XRC720TokenTransferList count={5} stats={stats} type={stats?.type}/>)}
                            {["balances"].includes(tab) && (<XRC720TokenHolderList count={5} type={stats?.type}/>)}
                            {["inventory"].includes(tab) && <XRC721InventoryList/>}
                            {["tokenTrade"].includes(tab) && <DEXTrades/>}
                            {["tokenNFTTrade"].includes(tab) && <NFTTrades/>}
                            {["info"].includes(tab) && <Info extra={extra} stats={stats}/>}
                            {["market"].includes(tab) && <MarketTable marketData={stats}/>}
                            {["code", "readContract", "readProxyContract", "writeContract", "writeProxyContract", "aiAuditAnalysis"].includes(tab) &&
                                <ContractDetails data={addressStats}/>}
                            {["analysis"].includes(tab) && <AnalysisTab/>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TokenDetails;
